import { useCallback, useEffect, useRef, useState } from 'react'
import {
  searchMultiPage,
  type TmdbSearchPage,
  type TmdbSearchResult,
} from '../tmdb/tmdb'
import { getEffectiveTmdbApiKey, useUserSettings } from '../settings/useUserSettings'

const DEBOUNCE_MS = 300

export interface UseTmdbSearchResult {
  results: TmdbSearchResult[]
  loading: boolean
  loadingMore: boolean
  error: string | null
  page: number
  totalPages: number
  hasMore: boolean
  /**
   * Force-run the search immediately (skipping the debounce).
   * Used for Enter key and manual "Search" button submissions.
   */
  runNow: () => void
  /** Append the next page of results, if available. */
  loadMore: () => void
  /** Reset to a clean empty state. */
  reset: () => void
}

/**
 * Debounced TMDB /search/multi with:
 *   - AbortController cancellation on every re-run (no stale results)
 *   - Race-condition safety via a monotonic request id (only the most
 *     recent request's result is applied)
 *   - Pagination via a "Load more" method that appends the next page
 *
 * Mirrors the desktop `ExternalSearchBar` + `SearchResults` contract.
 * When `query` is empty or no TMDB key is configured, the hook holds a
 * clean empty state and never fires a request.
 */
export function useTmdbSearch(query: string): UseTmdbSearchResult {
  const { settings } = useUserSettings()
  const [results, setResults] = useState<TmdbSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)

  // Race-condition safety: each request increments this counter and only
  // the most recent request's result is allowed to touch state.
  const requestIdRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  // Force-run counter — bumping it triggers the effect to execute the
  // search immediately rather than waiting for the debounce window.
  const [runTick, setRunTick] = useState(0)

  const trimmed = query.trim()
  // Falls back to the bundled developer key when the user hasn't supplied one.
  const apiKey = getEffectiveTmdbApiKey(settings)
  const language = settings?.tmdbPreferredLanguage || 'en'

  // Cancel any in-flight request on query change / unmount.
  const cancelInFlight = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!trimmed) {
      cancelInFlight()
      setResults([])
      setLoading(false)
      setLoadingMore(false)
      setError(null)
      setPage(1)
      setTotalPages(0)
      return
    }
    if (!apiKey) {
      setError('Add a TMDB API key in Settings to search.')
      setResults([])
      setLoading(false)
      setPage(1)
      setTotalPages(0)
      return
    }
    if (trimmed.length < 2) {
      // Match desktop's minimum-character behaviour: don't actually fire
      // a request until the query is at least 2 characters long.
      setResults([])
      setLoading(false)
      setError(null)
      setPage(1)
      setTotalPages(0)
      return
    }

    const debounceMs = runTick > 0 ? 0 : DEBOUNCE_MS
    const handle = setTimeout(() => {
      cancelInFlight()
      const controller = new AbortController()
      abortRef.current = controller
      const myRequestId = ++requestIdRef.current

      setLoading(true)
      setError(null)

      searchMultiPage(trimmed, apiKey, language, 1, controller.signal)
        .then((pageData: TmdbSearchPage) => {
          // Drop the result if a newer request has already fired.
          if (myRequestId !== requestIdRef.current) return
          setResults(pageData.results)
          setPage(pageData.page)
          setTotalPages(pageData.totalPages)
          setLoading(false)
        })
        .catch((err) => {
          if ((err as any)?.name === 'AbortError') return
          if (myRequestId !== requestIdRef.current) return
          console.warn('[useTmdbSearch]', err)
          setError(
            err instanceof Error ? err.message : 'Search failed — try again'
          )
          setLoading(false)
        })
    }, debounceMs)

    return () => {
      clearTimeout(handle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed, apiKey, language, runTick])

  useEffect(() => {
    return () => {
      cancelInFlight()
    }
  }, [cancelInFlight])

  const runNow = useCallback(() => {
    setRunTick((t) => t + 1)
  }, [])

  const loadMore = useCallback(() => {
    if (!trimmed || !apiKey) return
    if (loading || loadingMore) return
    if (page >= totalPages) return

    const nextPage = page + 1
    const controller = new AbortController()
    abortRef.current = controller
    const myRequestId = ++requestIdRef.current
    setLoadingMore(true)

    searchMultiPage(trimmed, apiKey, language, nextPage, controller.signal)
      .then((pageData: TmdbSearchPage) => {
        if (myRequestId !== requestIdRef.current) return
        // Dedupe by (kind, tmdbId) so a race doesn't duplicate rows.
        setResults((prev) => {
          const seen = new Set(prev.map((r) => `${r.kind}:${r.tmdbId}`))
          const merged = [...prev]
          for (const r of pageData.results) {
            const key = `${r.kind}:${r.tmdbId}`
            if (!seen.has(key)) {
              merged.push(r)
              seen.add(key)
            }
          }
          return merged
        })
        setPage(pageData.page)
        setTotalPages(pageData.totalPages)
        setLoadingMore(false)
      })
      .catch((err) => {
        if ((err as any)?.name === 'AbortError') return
        if (myRequestId !== requestIdRef.current) return
        console.warn('[useTmdbSearch] loadMore', err)
        setLoadingMore(false)
      })
  }, [trimmed, apiKey, language, page, totalPages, loading, loadingMore])

  const reset = useCallback(() => {
    cancelInFlight()
    setResults([])
    setError(null)
    setLoading(false)
    setLoadingMore(false)
    setPage(1)
    setTotalPages(0)
  }, [cancelInFlight])

  return {
    results,
    loading,
    loadingMore,
    error,
    page,
    totalPages,
    hasMore: page > 0 && page < totalPages,
    runNow,
    loadMore,
    reset,
  }
}
